import {
  DefaultSchoolYear,
  FilterKeys,
  GetMessagesQueryFilters,
  MessageArrangeBy,
  MessageStatus,
  MessageType,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  ArrangeBySelect,
  FiltersSidebar,
  FiltersSidebarMethods,
  MessageStatusSelectMultiple,
  MessageTypeSelectMultiple,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
  UserSelectMultiple,
} from '@schooly/components/filters';
import { PROPERTIES_TEXT_IDS } from '@schooly/constants';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useMessagesFilters } from './useMessagesFilters';

type MessagesFiltersSidebarProps = {
  defaultSchoolYear?: DefaultSchoolYear;
  filters: GetMessagesQueryFilters;
  arrangeBy: MessageArrangeBy | null;
  defaultFilters: GetMessagesQueryFilters;
  schoolId: string;
  onApply: (v: { filters: GetMessagesQueryFilters; arrangeBy: MessageArrangeBy | null }) => void;
};

export const MessagesFiltersSidebar = forwardRef<
  FiltersSidebarMethods,
  MessagesFiltersSidebarProps
>(
  (
    {
      filters: propsFilters,
      defaultFilters,
      onApply,
      schoolId,
      arrangeBy: propsArrangeBy,
      defaultSchoolYear,
    },
    ref,
  ) => {
    const [filters, setFilters] = useState(propsFilters);
    const [arrangeBy, setArrangeBy] = useState(propsArrangeBy);
    const { currentStaff, permissions } = useAuth();
    const { formatMessage } = useIntl();
    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
    const periodsRef = useRef<SchoolPeriodsRangeSelectComponent>(null);
    const {
      labels,
      arrangeByDropdownOptions,
      onSetDate,
      onSetMessageStatus,
      onClearMessageStatus,
      onSetMessageType,
      onClearMessageType,
      onSetCreator,
      onClearCreator,
    } = useMessagesFilters({ schoolId, filters, onSetFilters: setFilters });

    const handleApply = useCallback(() => {
      onApply({ filters, arrangeBy });
    }, [filters, onApply, arrangeBy]);

    const handleClear = useCallback(() => {
      setFilters(defaultFilters);
      setArrangeBy(null);
      if (defaultFilters.date) {
        periodsRef.current?.updateDates(defaultFilters.date);
      }
    }, [defaultFilters]);

    const onSetCreatorId = useCallback(
      (creatorId: string) => {
        onSetCreator(toggleMultipleValueArrayProperty(filters.creator, [creatorId]));
      },
      [filters.creator, onSetCreator],
    );

    const onSelectMessageStatus = useCallback(
      (status: MessageStatus) => {
        onSetMessageStatus(toggleMultipleValueArrayProperty(filters.message_status, [status]));
      },
      [filters.message_status, onSetMessageStatus],
    );

    const onSelectMessageType = useCallback(
      (type: MessageType) => {
        onSetMessageType(toggleMultipleValueArrayProperty(filters.message_type, [type]));
      },
      [filters.message_type, onSetMessageType],
    );

    return (
      <FiltersSidebar
        onOpen={() => {
          setFilters(propsFilters);
          setArrangeBy(propsArrangeBy);
        }}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-Messages' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <SchoolPeriodsRangeSelect
          ref={periodsRef}
          showSelectedPeriodLabel={false}
          schoolId={schoolId}
          defaultSchoolYear={defaultSchoolYear}
          hasManagePermission={permissions.includes('school_admin')}
          onSetDate={onSetDate}
          placeholder={labels[FilterKeys.Date]}
          date={filters.date || []}
          requiredLabel="required"
          onOpen={preventClose}
          onClose={allowClose}
        />

        <MessageStatusSelectMultiple
          placeholder={labels[FilterKeys.MessagesStatus]}
          onSelectMessageStatus={onSelectMessageStatus}
          selectedStatuses={filters.message_status || []}
          onClear={onClearMessageStatus}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <MessageTypeSelectMultiple
          placeholder={labels[FilterKeys.MessagesType]}
          onSelectMessageType={onSelectMessageType}
          selectedTypes={filters.message_type || []}
          onClear={onClearMessageType}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <UserSelectMultiple
          type="staff"
          currentUser={currentStaff}
          placeholder={labels[FilterKeys.Creator]}
          schoolId={schoolId}
          onSelectUserId={onSetCreatorId}
          selectedIds={filters.creator || []}
          onClear={onClearCreator}
          filters={filters}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <ArrangeBySelect
          options={arrangeByDropdownOptions}
          selectedOption={arrangeBy ?? undefined}
          placeholder={labels.arrangeBy}
          onClear={() => setArrangeBy(null)}
          onSelectOption={setArrangeBy}
          onOpen={preventClose}
          onClose={allowClose}
          getOptionLabelId={(o) =>
            o === FilterKeys.Creator ? 'messages-Sender' : PROPERTIES_TEXT_IDS[o]
          }
        />
      </FiltersSidebar>
    );
  },
);
